<script setup>
    import { ref, onMounted } from 'vue';
    import { useStore } from 'vuex';

    import Alerts from '@/api/alerts';
    import GenericTable2 from '@/components/tables/GenericTable2.vue';
    import CreateAlertModal from '@/components/modals/CreateAlert.vue';

    const loading = ref(true);
    const editingAlert = ref(false);
    const myAlertsRaw = ref([]);
    const myAlerts = ref([]);
    const sharedWithMeRaw = ref([]);
    const sharedWithMe = ref([]);
    const selectedMyAlerts = ref([]);
    const selectedSharedAlerts = ref([]);
    const modalOpen = ref(false);
    const alertDetails = ref(null);
    const store = useStore();
    const user = store.state.user;

    onMounted(async () => {
        await getAlerts();
        loading.value = false;
    })

    const getAlerts = async () => {
        const response = await Alerts.get();
        const alerts = response.details;

        // filter alerts by createdBy
        myAlertsRaw.value = alerts.filter(alert => alert.createdBy === user.uid);
        sharedWithMeRaw.value = alerts.filter(alert => alert.createdBy !== user.uid);

        myAlerts.value = myAlertsRaw.value.map(alert => ({
            name: alert.name,
            type: alert.type,
            "Alerts Every": alert.interval === 0 ? 'Once per event' : `${Math.floor(alert.interval/86400)} days, ${Math.floor((alert.interval%86400)/3600)} hours, ${Math.floor((alert.interval%3600)/60)} minutes`,
            description: alert.description,
        }));

        sharedWithMe.value = sharedWithMeRaw.value.map(alert => ({
            name: alert.name,
            type: alert.type,
            "Alerts Every": alert.interval === 0 ? 'Once per event' : `${Math.floor(alert.interval/86400)} days, ${Math.floor((alert.interval%86400)/3600)} hours, ${Math.floor((alert.interval%3600)/60)} minutes`,
            description: alert.description,
        }));
    }

    const createAlert = async (alertDetails) => {
        // Edit existing alert
        if (editingAlert.value) {
            await Alerts.put(alertDetails);
            editingAlert.value = false;
        }
        
        // Create new alert
        else {
            alertDetails.createdBy = user.uid;
            await Alerts.post(alertDetails);
        }
        modalOpen.value = false;
        await getAlerts();
    }

    const editAlert = (alerts) => {
        editingAlert.value = true;
        alertDetails.value = alerts[0];
        modalOpen.value = true;
    }

    const deleteAlert = async (alerts) => {
        await Alerts.delete({
            alertID: alerts,
        });
        await getAlerts();
    }

    const myAlertsSelected = (rows) => {
        selectedMyAlerts.value = rows;
    }

    const sharedAlertsSelected = (rows) => {
        selectedSharedAlerts.value = rows;
    }
</script>


<template>
    <div class="flex flex-col md:flex-row items-start md:items-center gap-4 mb-6">
        <h1 class="text-3xl md:text-5xl underline font-bold">Alerts</h1>
        <button @click="modalOpen = true" class="default-button">+ add alert</button>
    </div>

    <!-- My Alerts Section -->
    <div class="mb-8 px-2 md:px-0 md:mr-10">
        <div class="border border-gray-800 dark:border-gray-200 rounded-md p-3 md:p-5">
            <div class="flex flex-col sm:flex-row justify-between gap-3 mb-5">
                <h2 class="text-xl md:text-2xl font-bold">My Alerts</h2>
                <div v-if="selectedMyAlerts.length > 0" class="flex gap-2">
                    <button v-if="selectedMyAlerts.length === 1" 
                            @click="editAlert(selectedMyAlerts.map(index => myAlertsRaw[index]))" 
                            class="default-button">
                        edit
                    </button>
                    <button @click="deleteAlert(selectedMyAlerts.map(index => myAlertsRaw[index].id))" 
                            class="default-button">
                        delete
                    </button>
                </div>
            </div>
            <div class="overflow-x-auto -mx-3 md:mx-0">
                <div class="min-w-full px-3 md:px-0">
                    <GenericTable2
                        :loading="loading"
                        :data="myAlerts"
                        :selectable="true"
                        :pagination="true"
                        @RowsSelected="myAlertsSelected"
                    />
                </div>
            </div>
        </div>
    </div>

    <!-- Shared With Me Section -->
    <div class="mb-8 px-2 md:px-0 md:mr-10">
        <div class="border border-gray-800 dark:border-gray-200 rounded-md p-3 md:p-5">
            <div class="flex flex-col sm:flex-row justify-between gap-3 mb-5">
                <h2 class="text-xl md:text-2xl font-bold">Shared With Me</h2>
                <div v-if="selectedSharedAlerts.length > 0" class="flex gap-2">
                    <button @click="deleteAlert(selectedSharedAlerts.map(index => sharedWithMeRaw[index].id))" 
                            class="default-button">
                        unsubscribe
                    </button>
                </div>
            </div>
            <div class="overflow-x-auto -mx-3 md:mx-0">
                <div class="min-w-full px-3 md:px-0">
                    <GenericTable2
                        :loading="loading"
                        :data="sharedWithMe"
                        :selectable="true"
                        :pagination="true"
                        @RowsSelected="sharedAlertsSelected"
                    />
                </div>
            </div>
        </div>
    </div>

    <CreateAlertModal v-if="modalOpen"
        :alert-details="alertDetails"
        @alert-details="createAlert"
        @close="modalOpen = false"
    />
</template>
